import { resolveMedia } from "../lib/api";
import { useState, useMemo } from "react";

/**
 * SafeImage — responsive image with WebP srcset + graceful fallback.
 *
 * Props:
 *  - src         : original PNG/JPG url ("/api/files/malo-cover.png" or "http…")
 *  - alt         : alt text (required for a11y + SEO)
 *  - className   : styling
 *  - priority    : if true, eager-load + fetchpriority=high (use for LCP image)
 *  - sizes       : CSS sizes attribute (default "(max-width: 768px) 100vw, 50vw")
 *  - width/height: intrinsic dimensions to reserve space & prevent CLS
 *
 * The WebP variants live next to the original at:
 *   /api/files/{stem}.400w.webp, .800w.webp, .webp (full)
 * The 1200w variant is only referenced for images known to be ≥1200px wide.
 * Generated by /app/backend/scripts/generate_webp.py.
 */
export default function SafeImage({
  src,
  alt = "",
  className = "",
  priority = false,
  sizes = "(max-width: 768px) 100vw, 50vw",
  width,
  height,
  fallbackSrc = null,
  ...rest
}) {
  const [failed, setFailed] = useState(false);
  const [swappedToFallback, setSwappedToFallback] = useState(false);

  const effectiveSrc = swappedToFallback && fallbackSrc ? fallbackSrc : src;
  const resolved = resolveMedia(effectiveSrc);
  const { webpSrcSet } = useMemo(() => {
    if (!resolved) return { webpSrcSet: null };
    // Only build WebP variants for our own uploads (contain /files/ path).
    const m = resolved.match(/^(.*\/files\/)([^./?#]+)\.(png|jpe?g)(.*)$/i);
    if (!m) return { webpSrcSet: null };
    const [, base, stem, , tail] = m;
    // Safe widths — the generator only produces variants smaller than the source,
    // so we advertise the two most useful sizes that virtually every source image has.
    const url = (variant) => `${base}${stem}.${variant}${tail}`;
    return {
      webpSrcSet: [
        `${url("400w.webp")} 400w`,
        `${url("800w.webp")} 800w`,
      ].join(", "),
    };
  }, [resolved]);

  if (!src || failed) return null;

  const handleError = () => {
    // If a fallbackSrc is provided and we haven't tried it yet, swap once.
    if (fallbackSrc && !swappedToFallback && fallbackSrc !== src) {
      setSwappedToFallback(true);
      return;
    }
    setFailed(true);
  };

  const imgProps = {
    src: resolved,
    alt,
    className,
    onError: handleError,
    loading: priority ? "eager" : "lazy",
    decoding: priority ? "sync" : "async",
    fetchPriority: priority ? "high" : "auto",
    width,
    height,
    ...rest,
  };

  if (!webpSrcSet) {
    // Remote / non-uploads image — just render <img>
    return <img {...imgProps} />;
  }

  return (
    <picture className="block w-full h-full">
      <source type="image/webp" srcSet={webpSrcSet} sizes={sizes} />
      <img {...imgProps} />
    </picture>
  );
}
